// ============================================================
// Mock SIM fleet — Netcom Connect SIM (multi-carrier eSIM)
// ============================================================
// Powered by KORE OmniSIM Reach under the hood (white-label, internal).
// Customer-facing this is the "Netcom Connect SIM": ONE eSIM that steers
// itself across Verizon · AT&T · T-Mobile (and international partners)
// and fails over automatically to the strongest available network.
//
// This is the flagship demo fleet for the Harbor Point Security persona
// (DEMO-HPS-001) — a customer that runs Starlink dishes AND Netcom
// Connect SIMs side by side. It is served client-side (sim-api.js mock
// branch) because the demo's real backend account has no SIMs.
//
// A JS module (not JSON) so lastSeenAt / carrier-switch timestamps are
// fresh on every page load — keeps "Last seen 5m ago" and "Failed over
// 2m ago" honest in a live demo instead of going stale at build time.
//
// COMPLIANCE: no cost / price / wholesale / margin fields anywhere — the
// value story here is resilience + uptime + coverage, never dollars
// (ASP Term Sheet § 3.2(a) + sanitizePricing at the API boundary).

import { luhnChecksum } from '../../lib/imei-decoder.js';

const minutes = (n) => new Date(Date.now() - n * 60_000).toISOString();
const hours   = (n) => new Date(Date.now() - n * 60 * 60_000).toISOString();
const days    = (n) => new Date(Date.now() - n * 24 * 60 * 60_000).toISOString();

// ── Networks the Netcom Connect SIM can steer across ────────────────
// Single source of truth for carrier identity (name + brand color + MCC/MNC)
// so the fleet rows and the SimDetail carrier history render consistently.
export const NETWORKS = {
  verizon:  { carrier: 'Verizon',  mccmnc: '311480', country: 'US', color: '#EE0000' },
  att:      { carrier: 'AT&T',     mccmnc: '310410', country: 'US', color: '#00A8E0' },
  tmobile:  { carrier: 'T-Mobile', mccmnc: '310260', country: 'US', color: '#E20074' },
  ee:       { carrier: 'EE',       mccmnc: '23430',  country: 'GB', color: '#00857D' },
  rogers:   { carrier: 'Rogers',   mccmnc: '302720', country: 'CA', color: '#DA291C' },
  telcel:   { carrier: 'Telcel',   mccmnc: '334020', country: 'MX', color: '#004C97' },
};

// Shared data pool (GB) the whole fleet draws from — a headline benefit of
// the Netcom Connect SIM: pooled data means no per-SIM overage, usage floats
// to wherever it's needed across the fleet.
export const POOL = { name: 'Netcom Connect Shared Pool', capGb: 250 };

// reason codes for an automatic carrier switch (shown in the SimDetail timeline)
const R = {
  signal:   'Stronger signal',
  outage:   'Primary network degraded',
  roam:     'Crossed coverage boundary',
  congest:  'Primary congested',
  policy:   'Routing policy (best network)',
};

// ── Device + live-connection enrichment ────────────────────────────
// So each SIM shows the REAL unit it's in and a NOC-grade live session,
// derived deterministically from fields the entries already carry
// (assetType, region, carrier, signal, iccid) — entries stay clean.

// stable pseudo-random 0..1 from a string seed (no Math.random → stable render)
function seed(str, salt = 0) {
  let h = (2166136261 ^ salt) >>> 0;
  for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  return ((h >>> 0) % 100000) / 100000;
}
const pick = (arr, s) => arr[Math.floor(s * arr.length) % arr.length];

// Build a valid 15-digit IMEI (TAC 8 + serial 6 + Luhn check) — reuses the
// single luhnChecksum source of truth from imei-decoder.js.
function imei(tac, serial) {
  const p = (tac + serial).slice(0, 14).padEnd(14, '0');
  return p + luhnChecksum(p);
}
// Realistic-looking but STABLE identifier from a sequential seed — keeps the
// GSMA issuer prefix, scrambles the tail so the fleet doesn't read as
// ...0001, ...0002, ...0003 to a telecom evaluator.
function realId(prefix, sequential, len) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < sequential.length; i++) h = Math.imul(h ^ sequential.charCodeAt(i), 16777619);
  const tail = String((h >>> 0)).padStart(len, '7').slice(-len);
  return (prefix + tail).slice(0, prefix.length + len);
}

// asset type → the device it's typically installed in (TACs match imei-decoder)
const ASSET_TAC = {
  'Field Truck':     '35888044', // Cradlepoint IBR900 — in-vehicle dual-modem router
  'IoT Gateway':     '35260907', // Quectel EG25-G — industrial gateway
  'Security Camera': '35260930', // Quectel EC25-AF — M2M / telematics
  'Cold Chain':      '35471510', // Nordic nRF9160 — LTE-M asset tracker
  'EV Charger':      '86741310', // Telit ME910C1-WW — LTE-M module
  'Asset Tracker':   '35260914', // Quectel BG95-M3 — LTE-M + GNSS
  'Rugged Tablet':   '35415521', // Samsung Galaxy Tab Active4 Pro
  'Drone Uplink':    '35260920', // Quectel RM502Q-AE — 5G module
  'Kiosk':           '35234511', // Apple iPad Cellular
  'Test':            '35209900', // Sierra Wireless EM7565
  'Spare':           null,
};
// modules that run 5G / LTE-M rather than plain LTE
const TAC_RADIO = { '35260920': '5G NR', '35888045': '5G NR', '35471510': 'LTE-M', '35260914': 'LTE-M', '86741310': 'LTE-M' };
const TAC_FW = {
  '35888044': 'NCOS 7.24.40', '35260907': 'EG25GGBR07A08M2G', '35260930': 'EC25AFFDR07A08M4G',
  '35471510': 'mfw 1.3.6', '86741310': '37.00.007', '35260914': 'BG95M3LAR02A03', '35415521': 'Android 14 · T636',
  '35260920': 'RM502QAEAAR11A04M4G', '35234511': 'iPadOS 17.5', '35209900': 'SWI9X50C',
};
// carrier → representative LTE / 5G bands (for the live "band" line)
const CARRIER_BANDS = {
  'Verizon':  { lte: ['B13 · 700 MHz', 'B66 · AWS-3', 'B2 · 1900 MHz'], nr: ['n77 · C-band', 'n5 · 850 MHz'] },
  'AT&T':     { lte: ['B12 · 700 MHz', 'B2 · 1900 MHz', 'B14 · FirstNet'], nr: ['n5 · 850 MHz', 'n77 · C-band'] },
  'T-Mobile': { lte: ['B71 · 600 MHz', 'B2 · 1900 MHz', 'B12 · 700 MHz'], nr: ['n71 · 600 MHz', 'n41 · 2.5 GHz'] },
  'Telcel':   { lte: ['B4 · AWS', 'B28 · 700 MHz'], nr: ['n78 · 3.5 GHz'] },
  'Rogers':   { lte: ['B7 · 2600 MHz', 'B66 · AWS-3'], nr: ['n78 · 3.5 GHz'] },
  'EE':       { lte: ['B20 · 800 MHz', 'B3 · 1800 MHz'], nr: ['n78 · 3.5 GHz'] },
};
// region → approx geo for the device-location map (exported; FleetMap reuses it)
export const REGION_GEO = {
  'Permian Basin, TX':  { lat: 31.85, lng: -102.37, place: 'Midland–Odessa, TX' },
  'Bakken, ND':         { lat: 47.81, lng: -103.62, place: 'Williston, ND' },
  'Gulf Coast, TX':     { lat: 29.30, lng: -94.79,  place: 'Galveston Bay, TX' },
  'Midland Yard, TX':   { lat: 31.99, lng: -102.08, place: 'Midland, TX' },
  'Williston Yard, ND': { lat: 48.14, lng: -103.62, place: 'Williston, ND' },
  'I-35 Corridor, TX':  { lat: 30.27, lng: -97.74,  place: 'Austin, TX' },
  'Laredo → Monterrey': { lat: 27.53, lng: -99.49,  place: 'Laredo, TX (border)' },
  'Midland, TX':        { lat: 31.99, lng: -102.08, place: 'Midland, TX' },
  'Midland Lab, TX':    { lat: 31.99, lng: -102.08, place: 'Midland, TX' },
  'Aberdeen, UK':       { lat: 57.15, lng: -2.09,   place: 'Aberdeen, UK' },
};

/**
 * Build a SIM row. `home` is the primary carrier key; `on` is the carrier key
 * it's currently attached to. When they differ the SIM has automatically
 * failed over — the UI surfaces that as a live "steered to <carrier>" state.
 * The builder also derives the installed device + a full live-session record.
 */
function sim(o) {
  const home = NETWORKS[o.home] || null;
  const on = o.on ? NETWORKS[o.on] : (o.state === 'active' ? home : null);
  const carrier = on ? on.carrier : null;
  const id = o.iccid;

  // ── installed unit (device) ──
  const tac = ASSET_TAC[o.assetType] ?? null;
  const deviceImei = tac ? imei(tac, ('00000' + id.slice(-6)).slice(-6)) : null;
  const firmware = tac ? (TAC_FW[tac] || null) : null;
  const radioTech = tac ? (TAC_RADIO[tac] || 'LTE') : null;

  // ── live session (only when attached to a network) ──
  const online = !!carrier;
  const rsrp = o.signalDbm ?? null;                                    // dBm (RSRP)
  // RSRQ + SINR both track RSRP (a strong cell → good quality) with a little
  // jitter, so an RF-savvy evaluator never sees contradictory metrics.
  const rsrq = rsrp != null ? Math.max(-19, Math.min(-4, Math.round(-6 + (rsrp + 66) * 0.2 - seed(id, 3) * 1.5))) : null; // dB
  const sinr = rsrp != null ? Math.max(-2, Math.round(2 + (rsrp + 100) * 0.35 + seed(id, 4) * 3)) : null; // dB
  const bands = carrier && CARRIER_BANDS[carrier] ? CARRIER_BANDS[carrier] : null;
  const band = bands ? (radioTech && radioTech.startsWith('5G') ? pick(bands.nr, seed(id, 1)) : pick(bands.lte, seed(id, 2))) : null;
  const imsi = online ? on.mccmnc + ('000000000' + Math.floor(seed(id, 5) * 1e9)).slice(-9) : null;
  const ipAddress = online ? `100.${64 + Math.floor(seed(id, 6) * 30)}.${Math.floor(seed(id, 7) * 254)}.${1 + Math.floor(seed(id, 8) * 253)}` : null;
  const apn = online ? 'netcom.connect' : null;
  const cell = online ? { enbId: 100000 + Math.floor(seed(id, 9) * 800000), cellId: 1 + Math.floor(seed(id, 10) * 250), pci: Math.floor(seed(id, 11) * 504) } : null;
  const sessionState = o.state === 'active' ? (o.mbUsedMtd > 15 || seed(id, 14) > 0.45 ? 'connected' : 'idle')
    : o.state === 'test' ? 'connected' : 'offline';
  const sessionStartedAt = sessionState !== 'offline' ? new Date(Date.now() - Math.floor(1 + seed(id, 15) * 70) * 3600_000).toISOString() : null;

  // ── device location (jittered within its region) ──
  const g = o.region ? REGION_GEO[o.region] : null;
  const location = g ? { lat: +(g.lat + (seed(id, 12) - 0.5) * 0.25).toFixed(4), lng: +(g.lng + (seed(id, 13) - 0.5) * 0.25).toFixed(4), place: g.place } : null;

  // Auto-tags for fleet organization (region / asset / plan).
  const regionShort = o.region ? o.region.split(/[,→]/)[0].trim() : null;
  const tags = [...new Set([o.assetType, regionShort, (o.plan || '').replace('Reach ', '').trim() || null].filter(Boolean))];

  return {
    iccid: realId('8901390', o.iccid, 12),
    eid: realId('89049032', o.eid, 24),
    label: o.label,
    assetType: o.assetType || null,
    tags,
    region: o.region || null,
    state: o.state,
    // current network attachment (null when suspended / not yet activated)
    activeNetwork: on ? { carrier: on.carrier, mccmnc: on.mccmnc, country: on.country } : null,
    // the SIM's primary/home network — when activeNetwork differs, it's failed over
    homeCarrier: home ? home.carrier : null,
    steered: !!(on && home && on.carrier !== home.carrier),
    signalDbm: o.signalDbm ?? null,
    mbUsedMtd: o.mbUsedMtd ?? 0,
    mbCapMtd: o.mbCapMtd ?? null,        // null when the SIM draws from the shared pool
    pooled: o.pooled ?? false,
    deviceImei,
    imeiLocked: !!deviceImei && o.state === 'active',
    firmware,
    radioTech,
    msisdn: o.msisdn || null,
    plan: o.plan || null,
    failovers30d: o.failovers30d ?? 0,
    uptime30d: o.uptime30d ?? null,      // % — resilience headline, not a price
    activatedAt: o.activatedAt || null,
    lastSeenAt: o.lastSeenAt ?? null,
    carrierHistory: o.carrierHistory || [],
    // ── live session ──
    session: {
      state: sessionState,
      startedAt: sessionStartedAt,
      rsrp, rsrq, sinr, band, imsi, ipAddress, apn, cell,
    },
    location,
  };
}

const SIMS = [
  // ── Field trucks — roam hard across the basin, fail over constantly ──
  sim({
    iccid: '8901390000000010001', eid: '89049032004008882600099091240001',
    label: 'Rig-Truck-PB-014', assetType: 'Field Truck', region: 'Permian Basin, TX',
    home: 'verizon', on: 'tmobile', state: 'active', signalDbm: -71,
    mbUsedMtd: 3820.4, pooled: true, deviceImei: '352099001861401', msisdn: '+14325550101',
    plan: 'Reach Pooled', failovers30d: 12, uptime30d: 99.98, activatedAt: '2025-08-11T15:22:00Z',
    lastSeenAt: minutes(2),
    carrierHistory: [
      { at: minutes(2),  carrier: 'T-Mobile', reason: R.signal },
      { at: hours(4),    carrier: 'Verizon',  reason: R.roam },
      { at: hours(19),   carrier: 'AT&T',     reason: R.congest },
      { at: days(2),     carrier: 'Verizon',  reason: R.policy },
    ],
  }),
  sim({
    iccid: '8901390000000010002', eid: '89049032004008882600099091240002',
    label: 'Rig-Truck-PB-021', assetType: 'Field Truck', region: 'Permian Basin, TX',
    home: 'verizon', on: 'verizon', state: 'active', signalDbm: -84,
    mbUsedMtd: 2971.0, pooled: true, deviceImei: '352099001861402', msisdn: '+14325550102',
    plan: 'Reach Pooled', failovers30d: 7, uptime30d: 99.95, activatedAt: '2025-08-11T15:22:00Z',
    lastSeenAt: minutes(1),
    carrierHistory: [
      { at: hours(6),  carrier: 'Verizon', reason: R.signal },
      { at: days(1),   carrier: 'AT&T',    reason: R.outage },
    ],
  }),
  sim({
    iccid: '8901390000000010003', eid: '89049032004008882600099091240003',
    label: 'Service-Van-BK-08', assetType: 'Field Truck', region: 'Bakken, ND',
    home: 'tmobile', on: 'verizon', state: 'active', signalDbm: -79,
    mbUsedMtd: 1544.8, pooled: true, deviceImei: '352099001861403', msisdn: '+17015550103',
    plan: 'Reach Pooled', failovers30d: 9, uptime30d: 99.9, activatedAt: '2025-09-02T11:00:00Z',
    lastSeenAt: minutes(4),
    carrierHistory: [
      { at: minutes(4), carrier: 'Verizon',  reason: R.signal },
      { at: hours(9),   carrier: 'Rogers',   reason: R.roam },
      { at: hours(30),  carrier: 'T-Mobile', reason: R.policy },
    ],
  }),

  // ── Wellsite / rig fixed IoT gateways ──
  sim({
    iccid: '8901390000000010010', eid: '89049032004008882600099091240010',
    label: 'Wellsite-Gateway-PB-101', assetType: 'IoT Gateway', region: 'Permian Basin, TX',
    home: 'att', on: 'att', state: 'active', signalDbm: -68,
    mbUsedMtd: 612.2, pooled: true, deviceImei: '352099001861410', msisdn: '+14325550110',
    plan: 'Reach Pooled', failovers30d: 2, uptime30d: 100, activatedAt: '2025-07-19T14:00:00Z',
    lastSeenAt: minutes(3),
    carrierHistory: [{ at: days(3), carrier: 'AT&T', reason: R.policy }],
  }),
  sim({
    iccid: '8901390000000010011', eid: '89049032004008882600099091240011',
    label: 'Wellsite-Gateway-PB-118', assetType: 'IoT Gateway', region: 'Permian Basin, TX',
    home: 'att', on: 'tmobile', state: 'active', signalDbm: -88,
    mbUsedMtd: 704.5, pooled: true, deviceImei: '352099001861411', msisdn: '+14325550111',
    plan: 'Reach Pooled', failovers30d: 5, uptime30d: 99.97, activatedAt: '2025-07-19T14:00:00Z',
    lastSeenAt: minutes(7),
    carrierHistory: [
      { at: minutes(7), carrier: 'T-Mobile', reason: R.outage },
      { at: hours(12),  carrier: 'AT&T',     reason: R.signal },
    ],
  }),
  sim({
    iccid: '8901390000000010012', eid: '89049032004008882600099091240012',
    label: 'Rig-SCADA-GC-004', assetType: 'IoT Gateway', region: 'Gulf Coast, TX',
    home: 'verizon', on: 'verizon', state: 'active', signalDbm: -73,
    mbUsedMtd: 988.1, pooled: true, deviceImei: '352099001861412', msisdn: '+14095550112',
    plan: 'Reach Pooled', failovers30d: 3, uptime30d: 99.99, activatedAt: '2025-06-30T09:00:00Z',
    lastSeenAt: minutes(2),
    carrierHistory: [{ at: hours(16), carrier: 'Verizon', reason: R.signal }],
  }),

  // ── Perimeter / site security cameras ──
  sim({
    iccid: '8901390000000010020', eid: '89049032004008882600099091240020',
    label: 'Perimeter-Cam-PB-31', assetType: 'Security Camera', region: 'Permian Basin, TX',
    home: 'tmobile', on: 'tmobile', state: 'active', signalDbm: -76,
    mbUsedMtd: 4210.9, pooled: true, deviceImei: '352099001861420', msisdn: '+14325550120',
    plan: 'Reach Pooled', failovers30d: 4, uptime30d: 99.94, activatedAt: '2025-10-05T18:00:00Z',
    lastSeenAt: minutes(1),
    carrierHistory: [
      { at: hours(3),  carrier: 'T-Mobile', reason: R.signal },
      { at: days(1),   carrier: 'Verizon',  reason: R.congest },
    ],
  }),
  sim({
    iccid: '8901390000000010021', eid: '89049032004008882600099091240021',
    label: 'Perimeter-Cam-BK-12', assetType: 'Security Camera', region: 'Bakken, ND',
    home: 'verizon', on: 'att', state: 'active', signalDbm: -82,
    mbUsedMtd: 3760.2, pooled: true, deviceImei: '352099001861421', msisdn: '+17015550121',
    plan: 'Reach Pooled', failovers30d: 8, uptime30d: 99.88, activatedAt: '2025-10-05T18:00:00Z',
    lastSeenAt: minutes(5),
    carrierHistory: [
      { at: minutes(5), carrier: 'AT&T',    reason: R.outage },
      { at: hours(7),   carrier: 'Verizon', reason: R.signal },
      { at: hours(28),  carrier: 'Rogers',  reason: R.roam },
    ],
  }),
  sim({
    iccid: '8901390000000010022', eid: '89049032004008882600099091240022',
    label: 'Gate-Cam-GC-07', assetType: 'Security Camera', region: 'Gulf Coast, TX',
    home: 'att', on: 'att', state: 'active', signalDbm: -70,
    mbUsedMtd: 2890.6, pooled: true, deviceImei: '352099001861422', msisdn: '+14095550122',
    plan: 'Reach Pooled', failovers30d: 1, uptime30d: 100, activatedAt: '2025-11-01T12:00:00Z',
    lastSeenAt: minutes(2),
    carrierHistory: [{ at: days(4), carrier: 'AT&T', reason: R.policy }],
  }),

  // ── Cold-chain / logistics (cross-border) ──
  sim({
    iccid: '8901390000000010030', eid: '89049032004008882600099091240030',
    label: 'ColdChain-Reefer-114', assetType: 'Cold Chain', region: 'Laredo → Monterrey',
    home: 'att', on: 'telcel', state: 'active', signalDbm: -85,
    mbUsedMtd: 512.4, pooled: true, deviceImei: '352099001861430', msisdn: '+15125550130',
    plan: 'Reach Global', failovers30d: 14, uptime30d: 99.92, activatedAt: '2025-09-20T08:14:00Z',
    lastSeenAt: minutes(6),
    carrierHistory: [
      { at: minutes(6), carrier: 'Telcel', reason: R.roam },
      { at: hours(5),   carrier: 'AT&T',   reason: R.roam },
      { at: hours(22),  carrier: 'Verizon', reason: R.signal },
    ],
  }),
  sim({
    iccid: '8901390000000010031', eid: '89049032004008882600099091240031',
    label: 'ColdChain-Reefer-119', assetType: 'Cold Chain', region: 'I-35 Corridor, TX',
    home: 'verizon', on: 'verizon', state: 'active', signalDbm: -77,
    mbUsedMtd: 438.0, pooled: true, deviceImei: '352099001861431', msisdn: '+15125550131',
    plan: 'Reach Global', failovers30d: 6, uptime30d: 99.96, activatedAt: '2025-09-20T08:14:00Z',
    lastSeenAt: minutes(9),
    carrierHistory: [
      { at: hours(2), carrier: 'Verizon',  reason: R.signal },
      { at: hours(14), carrier: 'T-Mobile', reason: R.congest },
    ],
  }),

  // ── EV / equipment chargers ──
  sim({
    iccid: '8901390000000010040', eid: '89049032004008882600099091240040',
    label: 'EV-Charger-Yard-A', assetType: 'EV Charger', region: 'Midland Yard, TX',
    home: 'verizon', on: 'verizon', state: 'active', signalDbm: -66,
    mbUsedMtd: 41.8, pooled: true, deviceImei: '352099001861440', msisdn: '+14325550140',
    plan: 'Reach IoT', failovers30d: 0, uptime30d: 100, activatedAt: '2026-01-12T14:00:00Z',
    lastSeenAt: minutes(4),
    carrierHistory: [{ at: days(6), carrier: 'Verizon', reason: R.policy }],
  }),
  sim({
    iccid: '8901390000000010041', eid: '89049032004008882600099091240041',
    label: 'EV-Charger-Yard-B', assetType: 'EV Charger', region: 'Williston Yard, ND',
    home: 'att', on: 'tmobile', state: 'active', signalDbm: -90,
    mbUsedMtd: 38.2, pooled: true, deviceImei: '352099001861441', msisdn: '+17015550141',
    plan: 'Reach IoT', failovers30d: 3, uptime30d: 99.99, activatedAt: '2026-01-12T14:00:00Z',
    lastSeenAt: minutes(14),
    carrierHistory: [
      { at: minutes(14), carrier: 'T-Mobile', reason: R.signal },
      { at: hours(20),   carrier: 'AT&T',     reason: R.policy },
    ],
  }),

  // ── Asset trackers (high count, low data) ──
  sim({
    iccid: '8901390000000010050', eid: '89049032004008882600099091240050',
    label: 'Asset-Tracker-PB-201', assetType: 'Asset Tracker', region: 'Permian Basin, TX',
    home: 'tmobile', on: 'tmobile', state: 'active', signalDbm: -80,
    mbUsedMtd: 9.4, pooled: true, deviceImei: '352099001861450', msisdn: '+14325550150',
    plan: 'Reach IoT', failovers30d: 2, uptime30d: 99.97, activatedAt: '2026-02-01T10:00:00Z',
    lastSeenAt: minutes(11),
    carrierHistory: [{ at: hours(8), carrier: 'T-Mobile', reason: R.signal }],
  }),
  sim({
    iccid: '8901390000000010051', eid: '89049032004008882600099091240051',
    label: 'Asset-Tracker-PB-202', assetType: 'Asset Tracker', region: 'Permian Basin, TX',
    home: 'verizon', on: 'verizon', state: 'active', signalDbm: -75,
    mbUsedMtd: 8.1, pooled: true, deviceImei: '352099001861451', msisdn: '+14325550151',
    plan: 'Reach IoT', failovers30d: 1, uptime30d: 100, activatedAt: '2026-02-01T10:00:00Z',
    lastSeenAt: minutes(16),
    carrierHistory: [{ at: days(2), carrier: 'Verizon', reason: R.policy }],
  }),
  sim({
    iccid: '8901390000000010052', eid: '89049032004008882600099091240052',
    label: 'Asset-Tracker-BK-118', assetType: 'Asset Tracker', region: 'Bakken, ND',
    home: 'att', on: 'rogers', state: 'active', signalDbm: -87,
    mbUsedMtd: 11.2, pooled: true, deviceImei: '352099001861452', msisdn: '+17015550152',
    plan: 'Reach IoT', failovers30d: 10, uptime30d: 99.9, activatedAt: '2026-02-01T10:00:00Z',
    lastSeenAt: minutes(8),
    carrierHistory: [
      { at: minutes(8), carrier: 'Rogers', reason: R.roam },
      { at: hours(11),  carrier: 'AT&T',   reason: R.signal },
    ],
  }),

  // ── Rugged field tablets ──
  sim({
    iccid: '8901390000000010060', eid: '89049032004008882600099091240060',
    label: 'Field-Tablet-Crew-A', assetType: 'Rugged Tablet', region: 'Permian Basin, TX',
    home: 'verizon', on: 'att', state: 'active', signalDbm: -83,
    mbUsedMtd: 1320.7, pooled: true, deviceImei: '352099001861460', msisdn: '+14325550160',
    plan: 'Reach Pooled', failovers30d: 6, uptime30d: 99.93, activatedAt: '2025-12-03T09:00:00Z',
    lastSeenAt: minutes(3),
    carrierHistory: [
      { at: minutes(3), carrier: 'AT&T',    reason: R.congest },
      { at: hours(6),   carrier: 'Verizon', reason: R.signal },
    ],
  }),
  sim({
    iccid: '8901390000000010061', eid: '89049032004008882600099091240061',
    label: 'Field-Tablet-Crew-B', assetType: 'Rugged Tablet', region: 'Gulf Coast, TX',
    home: 'tmobile', on: 'tmobile', state: 'active', signalDbm: -72,
    mbUsedMtd: 1102.3, pooled: true, deviceImei: '352099001861461', msisdn: '+14095550161',
    plan: 'Reach Pooled', failovers30d: 3, uptime30d: 99.98, activatedAt: '2025-12-03T09:00:00Z',
    lastSeenAt: minutes(6),
    carrierHistory: [{ at: hours(4), carrier: 'T-Mobile', reason: R.signal }],
  }),

  // ── Drone / survey ──
  sim({
    iccid: '8901390000000010070', eid: '89049032004008882600099091240070',
    label: 'Survey-Drone-Uplink-3', assetType: 'Drone Uplink', region: 'Permian Basin, TX',
    home: 'verizon', on: 'tmobile', state: 'active', signalDbm: -78,
    mbUsedMtd: 2640.5, pooled: true, deviceImei: '352099001861470', msisdn: '+14325550170',
    plan: 'Reach Pooled', failovers30d: 11, uptime30d: 99.91, activatedAt: '2026-03-14T13:00:00Z',
    lastSeenAt: minutes(2),
    carrierHistory: [
      { at: minutes(2), carrier: 'T-Mobile', reason: R.signal },
      { at: hours(3),   carrier: 'Verizon',  reason: R.policy },
    ],
  }),

  // ── International site (UK) ──
  sim({
    iccid: '8901390000000010080', eid: '89049032004008882600099091240080',
    label: 'Aberdeen-Depot-GW-1', assetType: 'IoT Gateway', region: 'Aberdeen, UK',
    home: 'ee', on: 'ee', state: 'active', signalDbm: -74,
    mbUsedMtd: 806.9, pooled: true, deviceImei: '352099001861480', msisdn: '+447410550180',
    plan: 'Reach Global', failovers30d: 2, uptime30d: 99.99, activatedAt: '2025-11-08T08:14:00Z',
    lastSeenAt: minutes(12),
    carrierHistory: [{ at: hours(10), carrier: 'EE', reason: R.policy }],
  }),

  // ── Lifecycle: suspended, spares, test (realism) ──
  sim({
    iccid: '8901390000000010090', eid: '89049032004008882600099091240090',
    label: 'Kiosk-Decommissioned-2', assetType: 'Kiosk', region: 'Midland, TX',
    home: 'verizon', state: 'suspended', signalDbm: null,
    mbUsedMtd: 0, pooled: false, deviceImei: '352099001861490', msisdn: '+14325550190',
    plan: 'Reach Pooled', failovers30d: 0, uptime30d: null, activatedAt: '2025-05-01T11:00:00Z',
    lastSeenAt: days(9),
    carrierHistory: [{ at: days(9), carrier: 'Verizon', reason: 'Suspended' }],
  }),
  sim({
    iccid: '8901390000000010091', eid: '89049032004008882600099091240091',
    label: 'Spare-Inventory-01', assetType: 'Spare', region: null,
    home: 'verizon', state: 'pre-billing', signalDbm: null,
    mbUsedMtd: 0, pooled: false, deviceImei: null, msisdn: null,
    plan: null, failovers30d: 0, uptime30d: null, activatedAt: null, lastSeenAt: null,
  }),
  sim({
    iccid: '8901390000000010092', eid: '89049032004008882600099091240092',
    label: 'Spare-Inventory-02', assetType: 'Spare', region: null,
    home: 'verizon', state: 'pre-billing', signalDbm: null,
    mbUsedMtd: 0, pooled: false, deviceImei: null, msisdn: null,
    plan: null, failovers30d: 0, uptime30d: null, activatedAt: null, lastSeenAt: null,
  }),
  sim({
    iccid: '8901390000000010093', eid: '89049032004008882600099091240093',
    label: 'Staging-Bench-Test-A', assetType: 'Test', region: 'Midland Lab, TX',
    home: 'att', on: 'att', state: 'test', signalDbm: -69,
    mbUsedMtd: 3.1, pooled: false, deviceImei: '352099001861493', msisdn: '+14325550193',
    plan: 'Reach IoT', failovers30d: 0, uptime30d: null, activatedAt: '2026-06-20T09:00:00Z',
    lastSeenAt: minutes(41),
    carrierHistory: [{ at: hours(2), carrier: 'AT&T', reason: R.policy }],
  }),
];

export default SIMS;
